import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Form, Row, Col } from 'react-bootstrap';
import { supplierService } from '../../services/api';
import { Supplier } from '../../types';
import { toast } from 'react-toastify';
import { PlusIcon, EditIcon, TrashIcon } from '../common/Icons';
import SupplierForm from './SupplierForm';

const SupplierList: React.FC = () => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchSuppliers();
  }, [search]);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const response = await supplierService.getAll({ search });
      setSuppliers(response.data.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des fournisseurs');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce fournisseur ?')) {
      try {
        await supplierService.delete(id);
        toast.success('Fournisseur supprimé avec succès');
        fetchSuppliers();
      } catch (error) {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Fournisseurs</h2>
        <Button variant="primary" onClick={() => setShowForm(true)}>
          <PlusIcon className="me-2" />
          Nouveau fournisseur
        </Button>
      </div>

      <Card>
        <Card.Header>
          <Row>
            <Col md={6}>
              <Form.Control
                type="text"
                placeholder="Rechercher un fournisseur..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center">Chargement...</div>
          ) : (
            <Table responsive>
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>ICE</th>
                  <th>Email</th>
                  <th>Téléphone</th>
                  <th>Solde</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {suppliers.map((supplier) => (
                  <tr key={supplier.id}>
                    <td>{supplier.name}</td>
                    <td>{supplier.ice}</td>
                    <td>{supplier.email}</td>
                    <td>{supplier.phone}</td>
                    <td className={parseFloat(supplier.balance.toString()) >= 0 ? 'text-success' : 'text-danger'}>
                      {parseFloat(supplier.balance.toString()).toFixed(2)} MAD
                    </td>
                    <td>
                      <Button 
                        variant="outline-warning" 
                        size="sm" 
                        className="me-1"
                        onClick={() => {
                          setEditingSupplier(supplier);
                          setShowForm(true);
                        }}
                      >
                        <EditIcon />
                      </Button>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => handleDelete(supplier.id)}
                      >
                        <TrashIcon />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <SupplierForm
        show={showForm}
        onHide={() => {
          setShowForm(false);
          setEditingSupplier(null);
        }}
        onSuccess={() => {
          fetchSuppliers();
          setShowForm(false);
          setEditingSupplier(null);
        }}
        supplier={editingSupplier}
      />
    </div>
  );
};

export default SupplierList;